/*
 * System Merge Rules (BIOENH-498):
 * 1. Must be same system type
 * 2. Must be same ground level
 * 3. Pool:          | volume1 - volume2 | < 5000
 *    Spa:           | volume1 - volume2 | < 200
 *    Swim Spa:      | volume1 - volume2 | < 1000
 */

/**
 * True iff any systems for a customer can be merged.
 * @param  {array} xs Array of systems.
 * @return {bool}     True iff any systems can be merged.
 */
export function canMerge(xs) {
  const mergeable = mergeableSystems(xs);

  return mergeable.length >= 2;
}

/**
 * List of systems which have at least one merge target.
 * @param  {array} xs Systems to look for merges from.
 * @return {array}    Systems which have at least one merge target.
 */
export function mergeableSystems(xs = []) {
  const types = xs.reduce((acc, x) => { // Aggregate by system type; only merge pools with pools, spas with spas, etc.
    if (!acc[x.typeID]) {
      acc[x.typeID] = [];
    }
    acc[x.typeID].push(x);
    return acc;
  }, {
    1: [],
    2: [],
    3: [],
  });

  const mergeable = Object.values(types)
    .map(t => _canMergeType(t))
    .reduce((acc, x) => {
      for(let elem of x) {
        acc.add(elem);
      }
      return acc;
    }, new Set());

  return [...mergeable];
}

/**
 * Set containing all systens in xs which have an absolute difference in volume < num with another system.
 * @param  {number} num Volume to be less than.
 * @param  {array} xs   Array of systems.
 * @return {Set}        All systems with at least one merge.
 */
function _volumeInRange(num, xs) {
  if (xs.length < 2) {
    return new Set();
  }

  const output = new Set();

  let i = 0;

  for(const x of xs) {
    for (const y of xs.slice(i + 1)) {
      if (Math.abs(x.volume - y.volume) <= num) {
        output.add(x);
        output.add(y);
      }
    }

    i += 1;
  }

  return output;
}

/**
 * Set of all systems in ys which have a volume within num distance of the volume in x.
 * @param  {number} num Absolute difference in volume must be less than this.
 * @param  {array} ys   All systems to compare against.
 * @param  {object} x   System that must be matched against
 * @return {Set}        Set of all systems from ys containing compatible matches to x.
 */
function _volumeInRangeForTarget(num, ys, x) {
  const output = new Set();

  for (const y of ys) {
    if (Math.abs(x.volume - y.volume) <= num) {
      output.add(x);
      output.add(y);
    }
  }

  return output;
}

const systemTypeToRanges = {
  1: _volumeInRange.bind(null, 5000),
  2: _volumeInRange.bind(null, 200),
  3: _volumeInRange.bind(null, 1000),
};

const systemTypeToTargetRange = {
  1: _volumeInRangeForTarget.bind(null, 5000),
  2: _volumeInRangeForTarget.bind(null, 200),
  3: _volumeInRangeForTarget.bind(null, 1000),
};


/**
 * Aggregate on groundLevel and return true if any group of systems with the same groundLevel has volumes within the range to be mergeable.
 * @param  {array} xs Array of systems
 * @return {Set}      Set of unique systems with the same groundLevel type have volumes in the correct range.
 */
function _canMergeType(xs) {
  if (xs.length < 2) {
    return new Set();
  }

  const groundLevelGroups = xs.reduce((acc, x) => {
    if (!x.groundLevel && x.typeID === 1) {
      return acc;
    } else if (!x.groundLevel) {
      if (!acc['no-type']) {
        acc['no-type'] = [];
      }
      acc['no-type'].push(x);
      return acc;
    }

    if (!acc[x.groundLevel]) {
      acc[x.groundLevel] = [];
    }
    acc[x.groundLevel].push(x);
    return acc;
  }, {});

  return Object.values(groundLevelGroups)
    .map(x => systemTypeToRanges[x[0].typeID](x))
    .reduce((acc, x) => {
      for (let elem of x) {
        acc.add(elem);
      }
      return acc;
    }, new Set());
}

/**
 * Array of systems that a given system can be merged with.
 * @param  {object} x  System that others must match with.
 * @param  {array} xs  All possible systems.
 * @return {aray}      All systems from xs that are compatible to merge with x.
 */
export function mergesForTarget(x, xs = []) {
  const viable = xs.filter(y => x.typeID === y.typeID && (x.groundLevel === y.groundLevel && x.groundLevel) || x.typeID !== 1);
  const inRange = systemTypeToTargetRange[x.typeID](viable, x);

  return [...inRange];
}
